/**
 * Card - Componente de visualización de prefactura en mobile
 *
 * Este componente muestra la información de una prefactura en formato de tarjeta
 * para la vista mobile de Invoices.
 */
import { Badge } from '@shared/components/ui/badge';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';

// 🎯 Función utilitaria para formatear fechas como dd/mm/yyyy
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

export interface CardProps {
  item: any; // Invoice
  className?: string;
  selectedColumns?: string[];
}

export const Card = React.memo(function Card({ item: invoice, className = '', selectedColumns }: CardProps) {
  const { t } = useTranslation('invoicesModule');
  const currency = useCurrency(); // 🎯 Obtener moneda del team desde /me

  // 🎯 Helper para verificar si una columna está visible
  const isColumnVisible = (columnId: string) => {
    if (!selectedColumns) return true;
    return selectedColumns.includes(columnId);
  };

  // 🎯 Determinar si está facturado (CLOSED)
  const isClosed = invoice.status === 'CLOSED';

  return (
    <div className={`py-4 border-b border-card-border hover:bg-muted/30 transition-colors ${className}`}>
      {/* Header - Código y Estado */}
      <div className="flex items-center gap-2 mb-2 justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium text-base">{invoice.code}</span>
          {/* Estado Facturado */}
          {isColumnVisible('status') && (
            <Badge className={`text-xs ${isClosed ? 'bg-bg-success text-success' : 'bg-bg-warning text-warning'}`}>
              {isClosed ? t('table.yes', 'Sí') : t('table.no', 'No')}
            </Badge>
          )}
        </div>
      </div>

      {/* Fecha de Creación */}
      {isColumnVisible('created_at') && invoice.created_at && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-complementary-text">{t('table.created_at')}:</span>
          <span className="text-sm">{formatDate(invoice.created_at)}</span>
        </div>
      )}

      {/* Periodo */}
      {isColumnVisible('period') && invoice.period && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-complementary-text">{t('table.period')}:</span>
          <span className="text-sm">{invoice.period}</span>
        </div>
      )}

      {/* Importe Envíos */}
      {isColumnVisible('shipping_amount') && invoice.shipping_amount !== undefined && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-complementary-text">{t('table.shipping_amount')}:</span>
          <span className="text-sm font-medium">
            {new Intl.NumberFormat('es-ES', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(invoice.shipping_amount || 0)}{' '}
            {currency}
          </span>
        </div>
      )}

      {/* Importe Prep. */}
      {isColumnVisible('preparation_amount') && invoice.preparation_amount !== undefined && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-complementary-text">{t('table.preparation_amount')}:</span>
          <span className="text-sm font-medium">
            {new Intl.NumberFormat('es-ES', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(invoice.preparation_amount || 0)}{' '}
            {currency}
          </span>
        </div>
      )}

      {/* Importe COD */}
      {isColumnVisible('cod_amount') && invoice.cod_amount !== undefined && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-complementary-text">{t('table.cod_amount')}:</span>
          <span className="text-sm font-medium">
            {new Intl.NumberFormat('es-ES', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(invoice.cod_amount || 0)}{' '}
            {currency}
          </span>
        </div>
      )}

      {/* Total Pre-factura */}
      {isColumnVisible('total_amount') && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-complementary-text">{t('table.total_amount')}:</span>
          <span className="text-sm font-semibold">
            {new Intl.NumberFormat('es-ES', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(invoice.total_amount || 0)}{' '}
            {currency}
          </span>
        </div>
      )}

      {/* N° Pedidos */}
      {isColumnVisible('num_orders') && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-complementary-text">{t('table.num_orders')}:</span>
          <span className="text-sm">{Array.isArray(invoice.shipments) ? invoice.shipments.length : 0}</span>
        </div>
      )}
    </div>
  );
});
